import { useAssetCloudResourceFields } from "hooks/useAssetCloudResourceFields";
import { useAssetDetailOverviewFields } from "hooks/useAssetFixedFields";
import { useAssetHostFields } from "hooks/useAssetHostFields";
import { useAssetImageFields } from "hooks/useAssetImageFields";
import { useAssetNetworkResourceFields } from "hooks/useAssetNetworkResourceFields";
import { useAssetOtherTypeFields } from "hooks/useAssetOtherTypeFields";
import { useAssetRepositoryFields } from "hooks/useAssetRepositoryFields";
import { capitalize, get, isFunction, isString, map } from "lodash";
import React, { ReactNode } from "react";
import { AssetEsDto } from "types/assetdetails";
import { CellRenderers } from "utils/tableUtils";

import ACMetadataPanel from "ac-components/ac-metadata-panel/ACMetadataPanel";
import ACSettingSectionCard from "ac-components/ac-setting-section-card/ACSettingSectionCard";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";

import { ASSET_TYPE_FIELD_DISPLAY_MAP, ASSET_TYPE_VALUES } from "../constants";

export type GridFields = {
  label: string;
  name: string;
  valueClassName?: string;
  span: number;
  path: string;
  formatter?: (value: string, data: AssetEsDto) => JSX.Element | string;
  extra?: ReactNode;
};

interface AssetOverviewSectionProps {
  assetDetails: AssetEsDto;
  isCorrelatedAsset?: boolean;
}

const AssetOverviewSection = ({
  assetDetails,
  isCorrelatedAsset = false,
}: AssetOverviewSectionProps) => {
  const assetHostFields = useAssetHostFields(assetDetails);
  const assetImageFields = useAssetImageFields(assetDetails);
  const assetCloudResourceFields = useAssetCloudResourceFields(assetDetails);
  const assetRepositoryFields = useAssetRepositoryFields(assetDetails);
  const assetOtherTypeFields = useAssetOtherTypeFields(assetDetails);
  const assetNetworkResourceFields =
    useAssetNetworkResourceFields(assetDetails);

  const TYPE_FIELD_MAP: { [key: string]: GridFields[] } = {
    [ASSET_TYPE_VALUES.HOST]: assetHostFields.map((field) => ({
      ...field,
      path: "host",
      span: 24,
    })),
    [ASSET_TYPE_VALUES.IMAGE]: assetImageFields.map((field) => ({
      ...field,
      path: "image",
      span: 24,
    })),
    [ASSET_TYPE_VALUES.CLOUD_RESOURCE]: assetCloudResourceFields.map(
      (field) => ({
        ...field,
        path: "cloud resource",
        span: 24,
      }),
    ),
    [ASSET_TYPE_VALUES.REPOSITORY]: assetRepositoryFields.map((field) => ({
      ...field,
      path: "repository",
      span: 24,
    })),
    [ASSET_TYPE_VALUES.NETWORK_RESOURCE]: assetNetworkResourceFields.map(
      (field) => ({
        ...field,
        path: "networkResource",
        span: 24,
      }),
    ),
    ...(assetOtherTypeFields.length > 0
      ? {
          OTHERS: assetOtherTypeFields.map((field) => ({
            ...field,
            path: "other",
            span: 24,
          })),
        }
      : {}),
  };

  const overviewFields: GridFields[] = useAssetDetailOverviewFields(
    assetDetails,
    { isCorrelatedAsset },
  ).map((field) => ({
    ...field,
    path: "asset",
    span: 24,
  }));
  return (
    <div>
      <ACSettingSectionTitle title="Asset Details" />
      <ACSettingSectionCard>
        <div className="asset-details-container">
          {/* Left Section */}
          <div
            className="left-section layout-xs"
            style={
              !TYPE_FIELD_MAP[assetDetails.armorCodeType.toUpperCase()]
                ? { width: "100%" }
                : {}
            }
          >
            <div className="text-section-subtitle">Overview</div>
            <div className="layout-xs">
              {renderFieldRow(overviewFields, assetDetails)}
            </div>
          </div>
          {/* Right Section */}
          {TYPE_FIELD_MAP[assetDetails.armorCodeType.toUpperCase()] && (
            <div className="right-section layout-xs">
              <div className="text-section-subtitle">
                {ASSET_TYPE_FIELD_DISPLAY_MAP[assetDetails.armorCodeType] ??
                  capitalize(assetDetails.armorCodeType)}{" "}
                Details
              </div>
              <div className="layout-xs">
                {renderFieldRow(
                  TYPE_FIELD_MAP[assetDetails.armorCodeType.toUpperCase()],
                  assetDetails,
                )}
              </div>
            </div>
          )}
        </div>
      </ACSettingSectionCard>
    </div>
  );
};

export default AssetOverviewSection;

const renderFieldRow = (fields: GridFields[], data: AssetEsDto) => {
  return map(fields, (field) => {
    const { formatter, path, extra, label, name } = field;
    const value = get(data, path.split("."));
    let displayValue = CellRenderers.VALUE_OR_NA();

    if (isFunction(formatter)) {
      displayValue = formatter(value, data);
    }

    if (name.includes("header")) {
      return <div className="text-section-subtitle">{label}</div>;
    }

    return (
      <ACMetadataPanel.Row key={name}>
        <ACMetadataPanel.Label>
          <div className="text-ellipsis" title={displayTooltip(name, label)}>
            {label}
          </div>
        </ACMetadataPanel.Label>
        <ACMetadataPanel.Value title={displayTooltip(name, displayValue)}>
          {displayValue}
        </ACMetadataPanel.Value>
        {extra || null}
      </ACMetadataPanel.Row>
    );
  });
};

const displayTooltip = (key: string, value: any) => {
  if (key === "status") return value?.props?.children?.props?.label;
  if (key === "active") return "";
  if (isString(value)) return value;
  else return Array.isArray(value) ? value[0] : value?.props?.children;
};
